import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    'Food Safety Management & Auditing',
    'HACCP Implementation',
    'Supplier Audits',
    'Regulatory Compliance',
    'Label Compliance',
    'Food Safety Training',
    'Company Incorporation',
    'Tax & VAT Filing',
    'Bookkeeping & Financial Auditing',
    'Investment Planning',
    'Passive Income Planning',
    'Other'
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Location',
      details: 'The Netherlands'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'anushivi18@gmail.com'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: '+31 621660865'
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');

      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });

      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      setIsSubmitting(false);
      const detail = error?.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(detail);
    }
  };

  return (
    <div className="contact-page" style={{ backgroundColor: 'var(--color-cream)' }}>
      <SEO
        title="Contact Us"
        path="/contact"
        description="Get in touch with Prime Food & Financial Consultancy in the Netherlands. Contact us for food safety, compliance, and financial consultancy. Email anushivi18@gmail.com or call +31 621660865."
        keywords="contact Prime Food Financial Consultancy, food consultancy Netherlands contact, schedule consultation"
      />
      <Toaster position="top-center" richColors />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20" style={{ backgroundColor: 'var(--color-navy)' }}>
        <div className="container">
          <AnimatedSection>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="font-serif text-6xl md:text-7xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}>
                Get in Touch
              </h1>
              <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--color-cream)', opacity: 0.9 }}>
                Ready to transform your business? Let's start a conversation about how we can help you achieve your goals.
              </p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 -mt-16 relative z-10">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -10, boxShadow: '0 20px 50px rgba(0,0,0,0.15)' }}
                    className="bg-white p-8 text-center shadow-lg"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-emerald)' }}
                    >
                      <Icon size={28} style={{ color: 'white' }} />
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
                      {info.title}
                    </h3>
                    <p className="text-lg" style={{ color: 'var(--color-dark-gray)' }}>
                      {info.details}
                    </p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Form */}
            <AnimatedSection>
              <div className="bg-white p-8 md:p-12 shadow-xl">
                <h2 className="font-serif text-4xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                  Send Us a Message
                </h2>
                <p className="text-lg mb-8" style={{ color: 'var(--color-dark-gray)' }}>
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors"
                      style={{ borderColor: 'var(--color-gray)' }}
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors"
                      style={{ borderColor: 'var(--color-gray)' }}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors"
                      style={{ borderColor: 'var(--color-gray)' }}
                      placeholder="+31 123 456 789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors"
                      style={{ borderColor: 'var(--color-gray)' }}
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Service Interested In *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors"
                      style={{ borderColor: 'var(--color-gray)', color: 'var(--color-navy)' }}
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-navy)' }}>
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border-2 focus:outline-none focus:border-emerald-600 transition-colors resize-none"
                      style={{ borderColor: 'var(--color-gray)' }}
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting || isSubmitted}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    style={{
                      opacity: isSubmitting || isSubmitted ? 0.7 : 1
                    }}
                  >
                    {isSubmitted ? (
                      <>
                        <CheckCircle size={20} />
                        <span>Message Sent!</span>
                      </>
                    ) : isSubmitting ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Send Message</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </AnimatedSection>

            {/* Additional Info */}
            <AnimatedSection delay={0.3}>
              <div className="lg:sticky lg:top-32">
                <div className="mb-12">
                  <h3 className="font-serif text-3xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                    Why Contact Us?
                  </h3>
                  <div className="space-y-4">
                    {[
                      'Free initial consultation',
                      'Tailored solutions for your business',
                      'Expert guidance from experienced consultants',
                      'Quick response within 24 hours',
                      'Confidential and professional service'
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle size={24} className="flex-shrink-0 mt-1" style={{ color: 'var(--color-emerald)' }} />
                        <span className="text-lg" style={{ color: 'var(--color-dark-gray)' }}>{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-8 border-l-4 shadow-lg"
                  style={{ borderLeftColor: 'var(--color-gold)' }}
                >
                  <h4 className="font-serif text-2xl font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>
                    Business Hours
                  </h4>
                  <div className="space-y-2" style={{ color: 'var(--color-dark-gray)' }}>
                    <p className="flex justify-between">
                      <span className="font-medium">Monday - Friday</span>
                      <span>9:00 AM - 6:00 PM</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="font-medium">Saturday</span>
                      <span>9:00 AM - 5:00 PM</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="font-medium">Sunday</span>
                      <span>Closed</span>
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="mt-8 p-8 text-center"
                  style={{ backgroundColor: 'var(--color-emerald)', color: 'white' }}
                >
                  <h4 className="font-serif text-2xl font-semibold mb-3">
                    Prefer to Call?
                  </h4>
                  <p className="text-lg mb-4 opacity-90">
                    Schedule a phone consultation
                  </p>
                  <a href="tel:+31621660865" className="text-2xl font-bold hover:underline">
                    +31 621660865
                  </a>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <AnimatedSection>
        <section className="py-16" style={{ backgroundColor: 'var(--color-navy)' }}>
          <div className="container text-center">
            <h3 className="font-serif text-4xl font-bold mb-4" style={{ color: 'var(--color-gold)' }}>
              Based in The Netherlands
            </h3>
            <p className="text-xl" style={{ color: 'var(--color-cream)', opacity: 0.9 }}>
              Serving businesses across Europe and internationally
            </p>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Contact;
